// AB#2041 — imperative toast primitive shared across the Marque SPAs. A module-level singleton store
// (the same pattern as Dialogs.tsx's DialogHost): call sites just `toast.error('…')` with no
// per-component wiring; each app mounts <ToastHost /> once in its persistent layout. Toasts
// auto-dismiss after ~5s (with a manual dismiss escape hatch); the store is renderer-agnostic so it
// unit-tests with fake timers alone.

// AB#3019 — 'warning' is the variant for a SUCCEEDED operation that has something to say (a save
// that raised an advisory validate rule). Distinct from 'error', which means it did not happen,
// and from 'info', which carries no concern at all — the difference matters to a user deciding
// whether to go back and look at what they just saved.
export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

// AB#3966 — one optional action per toast (the cockpit's "Removed X — Undo"). Rendered by ToastHost
// as a button beside the message; activating it dismisses the toast and runs the handler. A toast
// with an action is deliberately NOT a modal confirmation: the operation has already happened and the
// action reverses it, so the toast may still expire on its own (callers pass a longer durationMs).
export interface ToastAction {
    label: string
    onClick: () => void
}

export interface ToastItem {
    id: number
    message: string
    variant: ToastVariant
    action?: ToastAction | undefined
}

export interface ToastOptions {
    variant?: ToastVariant
    /** Auto-dismiss delay in ms. Defaults to {@link TOAST_DURATION_MS}. */
    durationMs?: number
    /** AB#3966 — an action button beside the message (Undo). */
    action?: ToastAction | undefined
}

export const TOAST_DURATION_MS = 5000

let toasts: readonly ToastItem[] = []
let nextId = 1
const timers = new Map<number, ReturnType<typeof setTimeout>>()
const listeners = new Set<() => void>()
const emit = () => listeners.forEach(l => l())

export function subscribeToasts(listener: () => void): () => void {
    listeners.add(listener)
    return () => { listeners.delete(listener) }
}

/** Immutable snapshot for useSyncExternalStore — the reference changes only when the list changes. */
export function getToasts(): readonly ToastItem[] {
    return toasts
}

/** Removes a toast immediately and cancels its auto-dismiss timer. Unknown ids are a no-op. */
export function dismissToast(id: number): void {
    const timer = timers.get(id)
    if (timer !== undefined) { clearTimeout(timer); timers.delete(id) }
    if (toasts.some(t => t.id === id)) {
        toasts = toasts.filter(t => t.id !== id)
        emit()
    }
}

/** Removes every toast and cancels all timers (test isolation; sign-out). */
export function clearToasts(): void {
    timers.forEach(t => clearTimeout(t))
    timers.clear()
    if (toasts.length > 0) {
        toasts = []
        emit()
    }
}

function show(message: string, opts?: ToastOptions): number {
    const id = nextId++
    toasts = [...toasts, { id, message, variant: opts?.variant ?? 'info', action: opts?.action }]
    timers.set(id, setTimeout(() => dismissToast(id), opts?.durationMs ?? TOAST_DURATION_MS))
    emit()
    return id
}

/** `toast('Saved', { variant: 'success' })` — or the `toast.success/error/info` shorthands. */
export const toast = Object.assign(show, {
    success: (message: string): number => show(message, { variant: 'success' }),
    error: (message: string): number => show(message, { variant: 'error' }),
    info: (message: string): number => show(message, { variant: 'info' }),
    warning: (message: string): number => show(message, { variant: 'warning' }),
})
